import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Upload, BarChart3, PiggyBank } from "lucide-react";
import { usePartnerViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import SavingsCalculator from "@/components/SavingsCalculator";
import ContactForm from "@/components/ContactForm";
import TrustBar from "@/components/TrustBar";
import RelatedTools from "@/components/RelatedTools";
import SolutionsCrossSell from "@/components/SolutionsCrossSell";
import SocialProofStrip from "@/components/SocialProofStrip";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function SwitchAndSavePage() {
  usePartnerViewed("switch_and_save", { itemType: "partner_service" });
  return (
    <div>
      <SEOHead title="Switch & Save on Merchant Services" description="Ready to cut your transaction fees? Switch to CanDo Payments today and save money on your merchant services with our competitive, transparent pricing." path="/switch-and-save" />

      {/* CALCULATOR FIRST — above the fold */}
      <section className="py-10 md:py-16 bg-gray-50" id="calculator" data-testid="calculator-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-2">Switch & Save</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight mb-3">See how much you could save</h1>
            <p className="text-brand-grey leading-relaxed max-w-xl mx-auto">Enter your current card processing costs and get an instant side-by-side comparison with CanDo rates.</p>
          </div>
          <SavingsCalculator />
        </div>
      </section>

      <TrustBar />

      {/* Social proof — live Google rating + internal link to full review page */}
      <section className="py-8 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SocialProofStrip
            reassurance="Rated 4.92/5 by UK businesses"
            testId="switch-save-social-proof"
          />
        </div>
      </section>

      {/* Supporting content — value props */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0 }} variants={stagger} className="text-center mb-10">
            <motion.h2 variants={fadeUp} className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Simple, seamless switching</motion.h2>
            <motion.p variants={fadeUp} className="text-brand-grey max-w-2xl mx-auto">Our average customer saves 36%. The greatest saving so far: 64%. Your current solution stays live until we&apos;re ready to switch.</motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Upload, step: "01", title: "Submit your statement", desc: "Send us a copy of your current processing statement. We drop it into our analysis tool and review every charge." },
              { icon: BarChart3, step: "02", title: "We analyse & quote", desc: "We present your charges in an easy-to-understand format and show you exactly how much you can save." },
              { icon: PiggyBank, step: "03", title: "Enjoy the savings", desc: "We order your hardware, agree a go-live date, and you start saving. Your current solution stays live until we're ready." },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-brand-aqua text-white font-heading text-2xl font-bold flex items-center justify-center mx-auto mb-5">{s.step}</div>
                <h3 className="font-heading text-xl font-bold text-brand-black mb-3">{s.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why switch — trust messaging */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-brand-black mb-6">Why businesses switch to CanDo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto">
            {["No obligation — just honest numbers", "We review ALL your charges, not just the headline rate", "Your current solution stays live until we switch you", "Greatest saving so far: 64%"].map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-brand-black"><CheckCircle size={16} className="text-brand-aqua flex-shrink-0 mt-0.5" />{item}</div>
            ))}
          </div>
        </div>
      </section>

      <RelatedTools currentPath="/switch-and-save" />
      <SolutionsCrossSell currentPath="/switch-and-save" />

      {/* Contact form at the bottom */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Ready to see what you can save?</h3>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
